import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Form } from 'antd';
import moment from 'moment';
import CreateMeetingComponent from '../../Components/CreateMeeting/CreateMeetingComponent';
import * as eventActions from '../../Actions/EventActions';
import { getEventsById, getEventsByDate } from '../../Reducers/EventReducer';
import { getSettings } from '../../Reducers/SettingsReducer';
import { getParameterValue } from '../../Repositories/CalendarRepository';

const mapStateToProps = state => {
  const settings = getSettings(state);
  return {
    defaultDuration: settings ? settings.SlotDuration : undefined,
    eventsById: getEventsById(state),
    eventsByDate: getEventsByDate(state),
  };
};

const mapDispatchToProps = {
  createEvent: eventActions.createEvent,
  updateEvent: eventActions.updateEvent,
  fetchCollisionEventsCount: eventActions.fetchCollisionEventsCount,
};

function getTimings(values, allDayCheckBoxEnabled) {
  const freeStart = moment(values.datePicker).set({
    hour: values.timePicker.hour(), minute: values.timePicker.minute(), second: 0, millisecond: 0,
  });
  let freeStartTime = freeStart.toDate();
  let freeEndTime = new Date(freeStartTime);
  if (!allDayCheckBoxEnabled) {
    if (values.slotTimeUnit === 'Minutes') {
      freeEndTime.setTime(freeStartTime.getTime() + values.timeSpan * 60000);
    } else if (values.slotTimeUnit === 'Hours') {
      freeEndTime.setTime(freeStartTime.getTime() + values.timeSpan * 3600000);
    } else if (values.slotTimeUnit === 'Days') {
      freeEndTime.setDate(freeStartTime.getDate() + values.timeSpan);
    } else {
      freeEndTime.setDate(freeStartTime.getDate() + values.timeSpan * 7);
    }
  } else {
    freeStartTime = new Date(`${values.datePicker.format('YYYY-MM-DD')}T00:00:00`);
    freeEndTime = new Date(`${values.datePicker.format('YYYY-MM-DD')}T23:59:59`);
  }
  return {
    startTime: freeStartTime,
    endTime: freeEndTime,
  };
}

export { getTimings };

class CreateMeeting extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      disable: false,
      eventsOverlapCount: 0,
    };
    this.showModal = this.showModal.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleOk = this.handleOk.bind(this);
    this.onClickMarked = this.onClickMarked.bind(this);
    this.createMeeting = this.createMeeting.bind(this);
    this.setEventsOverlapCount = this.setEventsOverlapCount.bind(this);
    this.handleEventTimeChange = this.handleEventTimeChange.bind(this);
  }

  onClickMarked = e => {
    this.setState({ disable: e.target.checked });
  };

  setEventsOverlapCount(addCount) {
    let { eventsOverlapCount } = this.state;
    eventsOverlapCount += addCount;
    this.setState({
      eventsOverlapCount,
    });
  }

  showModal = () => {
    this.setState({
      visible: true,
    });
  };

  handleOk = e => {
    e.preventDefault();
    const { form } = this.props;
    const { freeStartTime, freeEndTime } = this.state;
    form.validateFields((err, values) => {
      let ignoreError = false;
      if (err) {
        // temp fix for handiling circular dep in validation
        const errorFields = Object.keys(err);
        const dateTimeFields = new Set(['datePicker', 'timePicker']);
        const hasOnlyDateTimeRevalidateErrors = errorFields.every(
          field => dateTimeFields.has(field) &&
          err[field].errors?.length > 0 &&
          err[field].errors[0].message?.includes('need to revalidate')
        );

        if (hasOnlyDateTimeRevalidateErrors && errorFields.length > 0) {
          form.validateFields(['datePicker'], { force: true }, (secondErr) => {
            if (!secondErr) {
              ignoreError = true;
            }
          });
        }
      }

      if (!err || ignoreError) {
        let timings = {
          startTime: freeStartTime,
          endTime: freeEndTime,
        };
        if (!freeStartTime) {
          const allDayCheckBoxEnabled = form.getFieldValue('allDay');
          timings = getTimings(values, allDayCheckBoxEnabled);
        }
        this.createMeeting(values, timings.startTime, timings.endTime);
      }
    });
  };

  handleCancel = () => {
    const { form } = this.props;
    this.setState({
      visible: false,
      disable: false,
      eventsOverlapCount: 0,
    });
    form.resetFields();
  };

  handleEventTimeChange(value, formDecorator) {
    let allDayCheckBoxEnabled;
    const { form, type, event, fetchCollisionEventsCount } = this.props;
    this.setState({
      eventsOverlapCount: 0,
    });
    const values = {
      datePicker: form.getFieldValue('datePicker'),
      timePicker: form.getFieldValue('timePicker'),
      timeSpan: form.getFieldValue('timeSpan'),
      slotTimeUnit: form.getFieldValue('slotTimeUnit'),
    };
    if (value && formDecorator) {
      values[formDecorator] = value;
    } else if (value !== undefined) {
      allDayCheckBoxEnabled = value;
    }
    const timings = getTimings(values, allDayCheckBoxEnabled);
    const freeStartTime = timings.startTime;
    const freeEndTime = timings.endTime;
    this.setState({
      freeStartTime,
      freeEndTime,
    });
    const timeSlot = {
      start_date: freeStartTime.toISOString(),
      end_date: freeEndTime.toISOString(),
    };
    if (
      type === 'update' &&
      ((event.StartTime >= timeSlot.start_date && event.StartTime <= timeSlot.end_date) ||
        (event.EndTime > timeSlot.start_date && event.EndTime <= timeSlot.end_date) ||
        (timeSlot.start_date >= event.StartTime && timeSlot.start_date < event.EndTime))
    ) {
      fetchCollisionEventsCount(timeSlot, true, count => this.setEventsOverlapCount(count));
    } else {
      fetchCollisionEventsCount(timeSlot, false, count => this.setEventsOverlapCount(count));
    }
  }

  createMeeting(values, freeStartTime, freeEndTime) {
    const newEvent = {};
    const recruiterEmail = getParameterValue('username');
    const { type, createEvent, updateEvent, form, event } = this.props;
    if (type !== 'update') {
      const { requiredEmailIds, optionalEmailIds } = values;
      const attendeesList = [];
      requiredEmailIds.forEach( (email) => {
        attendeesList.push({
          Email: email,
          Role: 'REQ-PARTICIPANT'
        })
      });
      optionalEmailIds.forEach( (email) => {
        attendeesList.push({
          Email: email,
          Role: 'OPT-PARTICIPANT'
        })
      });
      newEvent.Location = values.location;
      newEvent.Description = values.body;
      newEvent.Summary = values.subject;
      newEvent.Attendees = attendeesList;
      const isRecruiterAlreadyAdded = _.findIndex(attendeesList, {'Email': getParameterValue('username')});
      if(isRecruiterAlreadyAdded === -1){
        newEvent.Attendees.push({ Email: recruiterEmail, Role: 'NON-PARTICIPANT' });
      }
    } else {
      newEvent.Attendees = event.Attendees;
      newEvent.AttendeeStatus = 'proposed';
      newEvent.EventId = event.EventId;
      newEvent.Status = 'waiting';
    }
    newEvent.EndTime = freeEndTime.toISOString();
    newEvent.StartTime = freeStartTime.toISOString();
    this.setState({
      visible: false,
      disable: false,
    });
    for (let index = 0; index < newEvent.Attendees.length; index += 1) {
      if (newEvent.Attendees[index].Email === recruiterEmail) {
        newEvent.Attendees[index].Status = 'Confirmed';
      } else {
        newEvent.Attendees[index].Status = 'Waiting';
      }
    }
    form.resetFields();
    if (type !== 'update') {
      createEvent(newEvent);
    } else {
      updateEvent(newEvent, 'Propose');
    }
  }

  render() {
    const { defaultDuration, candidates, type, form, event, title } = this.props;
    const { eventsOverlapCount, visible, disable } = this.state;
    return (
      <CreateMeetingComponent
        defaultDuration={defaultDuration}
        candidates={candidates}
        handleEventTimeChange={this.handleEventTimeChange}
        handleCancel={this.handleCancel}
        handleOk={this.handleOk}
        onClickMarked={this.onClickMarked}
        showModal={this.showModal}
        visible={visible}
        disable={disable}
        form={form}
        type={type}
        event={event}
        title={title}
        eventsOverlapCount={eventsOverlapCount}
      />
    );
  }
}

CreateMeeting.propTypes = {
  eventsByDate: PropTypes.shape({}),
  eventsById: PropTypes.shape({}),
  form: PropTypes.shape({
    validateFields: PropTypes.func,
    getFieldDecorator: PropTypes.func,
    resetFields: PropTypes.func,
    getFieldValue: PropTypes.func,
    setFieldsValue: PropTypes.func,
  }).isRequired,
  createEvent: PropTypes.func.isRequired,
  updateEvent: PropTypes.func.isRequired,
  fetchCollisionEventsCount: PropTypes.func.isRequired,
  candidates: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      email: PropTypes.string,
      role: PropTypes.string,
    })
  ),
  type: PropTypes.string,
  event: PropTypes.shape({
    EventId: PropTypes.string,
    Attendees: PropTypes.arrayOf(PropTypes.shape({})),
    StartTime: PropTypes.string,
    EndTime: PropTypes.string,
  }),
  title: PropTypes.string,
  defaultDuration: PropTypes.number,
};

CreateMeeting.defaultProps = {
  eventsById: {},
  eventsByDate: {},
  type: '',
  event: {
    EventId: '',
    Attendees: [],
    StartTime: '',
    EndTime: '',
  },
  title: '',
  defaultDuration: undefined,
  candidates: [],
};

const CreateMeetingContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(Form.create()(CreateMeeting));

export default CreateMeetingContainer;
